import {
  Box,
  Grid,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { memo } from "react";

interface GuestInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  roomType: string;
}

interface BookBotelCustomerDetailsFormProps {
  initialValues: GuestInfo;
  onChange: (values: GuestInfo) => void;
}

const CustomerDetailsForm = memo(
  ({ initialValues, onChange }: BookBotelCustomerDetailsFormProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const handleInputChange =
      (field: keyof GuestInfo) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        onChange({
          ...initialValues,
          [field]: newValue,
        });
      };

    return (
      <Box
        sx={{
          bgcolor: "#232933",
          borderRadius: isMobile ? 3 : 4,
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1200,
          mx: "auto",
          boxShadow: 6,
          color: "#fff",
          position: "relative",
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          mb={isMobile ? 2 : 3}
          sx={{
            fontSize: isSmallMobile
              ? "1.25rem"
              : isMobile
              ? "1.5rem"
              : "1.75rem",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          Guest Information
        </Typography>
        <Grid
          container
          spacing={isMobile ? 1.5 : 2}
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Grid size={isSmallMobile ? 12 : 6}>
            <Typography
              variant="subtitle2"
              mb={isMobile ? 0.5 : 1}
              sx={{
                fontSize: isSmallMobile ? "0.875rem" : "1rem",
                fontWeight: 500,
              }}
            >
              First Name
            </Typography>
            <TextField
              fullWidth
              value={initialValues.firstName}
              onChange={handleInputChange("firstName")}
              sx={{
                bgcolor: "#1a1f24",
                borderRadius: 2,
                input: {
                  color: "#fff",
                  fontSize: isSmallMobile ? "0.875rem" : "1rem",
                  padding: isSmallMobile ? "10px 12px" : "12px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#4a5568",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={isSmallMobile ? 12 : 6}>
            <Typography
              variant="subtitle2"
              mb={isMobile ? 0.5 : 1}
              sx={{
                fontSize: isSmallMobile ? "0.875rem" : "1rem",
                fontWeight: 500,
              }}
            >
              Last Name
            </Typography>
            <TextField
              fullWidth
              type="text"
              value={initialValues.lastName}
              onChange={handleInputChange("lastName")}
              sx={{
                bgcolor: "#1a1f24",
                borderRadius: 2,
                input: {
                  color: "#fff",
                  fontSize: isSmallMobile ? "0.875rem" : "1rem",
                  padding: isSmallMobile ? "10px 12px" : "12px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#4a5568",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={isSmallMobile ? 12 : 6}>
            <Typography
              variant="subtitle2"
              mb={isMobile ? 0.5 : 1}
              sx={{
                fontSize: isSmallMobile ? "0.875rem" : "1rem",
                fontWeight: 500,
              }}
            >
              Phone Number
            </Typography>
            <TextField
              fullWidth
              type="tel"
              value={initialValues.phone}
              onChange={handleInputChange("phone")}
              sx={{
                bgcolor: "#1a1f24",
                borderRadius: 2,
                input: {
                  color: "#fff",
                  fontSize: isSmallMobile ? "0.875rem" : "1rem",
                  padding: isSmallMobile ? "10px 12px" : "12px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#4a5568",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={isSmallMobile ? 12 : 6}>
            <Typography
              variant="subtitle2"
              mb={isMobile ? 0.5 : 1}
              sx={{
                fontSize: isSmallMobile ? "0.875rem" : "1rem",
                fontWeight: 500,
              }}
            >
              Email
            </Typography>
            <TextField
              fullWidth
              type="email"
              value={initialValues.email}
              onChange={handleInputChange("email")}
              sx={{
                bgcolor: "#1a1f24",
                borderRadius: 2,
                input: {
                  color: "#fff",
                  fontSize: isSmallMobile ? "0.875rem" : "1rem",
                  padding: isSmallMobile ? "10px 12px" : "12px 14px",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#4a5568",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }
);

export default function BookBotelCustomerDetailsForm(
  props: BookBotelCustomerDetailsFormProps
) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "transparent",
        pt: { xs: 2, sm: 3, md: 8 },
        maxWidth: 1200,
        mx: "auto",
        pb: isMobile ? 2 : 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: isSmallMobile ? 2 : isMobile ? 3 : 4,
      }}
    >
      <CustomerDetailsForm {...props} />
    </Box>
  );
}
